/**
 * Períodos de consulta (dia / semana / mês) no fuso horário da fábrica.
 *
 * O servidor pode rodar em UTC; o "dia" que importa para a gestão e para o
 * operador é o dia local (Franca/SP). O fuso é cadastro da empresa
 * (`ConfiguracaoFabrica.fusoHorario`), nunca constante no código.
 *
 * Implementado só com `Intl` para não trazer dependência de calendário.
 */
/** Fuso padrão de uma empresa nova; o cadastro (`ConfiguracaoFabrica`) pode trocar. */
export const FUSO_HORARIO_PADRAO = 'America/Sao_Paulo';
export const PERIODOS = ['dia', 'semana', 'quinzena', 'mes'];
const DIAS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const formatadores = new Map();
function formatador(fuso) {
    let f = formatadores.get(fuso);
    if (!f) {
        f = new Intl.DateTimeFormat('en-US', {
            timeZone: fuso,
            hourCycle: 'h23',
            weekday: 'short',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
        });
        formatadores.set(fuso, f);
    }
    return f;
}
/** `true` se o identificador IANA é aceito pelo runtime (ex.: "America/Sao_Paulo"). */
export function fusoHorarioValido(fuso) {
    try {
        formatador(fuso);
        return true;
    }
    catch {
        return false;
    }
}
/** Decompõe um instante nas partes do relógio de parede do fuso. */
export function partesNoFuso(instante, fuso) {
    const partes = {};
    for (const p of formatador(fuso).formatToParts(instante))
        partes[p.type] = p.value;
    return {
        ano: Number(partes.year),
        mes: Number(partes.month),
        dia: Number(partes.day),
        hora: Number(partes.hour),
        minuto: Number(partes.minute),
        segundo: Number(partes.second),
        diaSemana: DIAS.indexOf(partes.weekday ?? ''),
    };
}
/**
 * Instante correspondente a um relógio de parede no fuso (inverso de
 * `partesNoFuso`). Resolve o deslocamento por aproximação em duas passadas,
 * o que cobre mudanças de horário de verão.
 */
export function instanteNoFuso(fuso, ano, mes, dia, hora = 0, minuto = 0, segundo = 0) {
    const alvo = Date.UTC(ano, mes - 1, dia, hora, minuto, segundo);
    let palpite = alvo;
    for (let i = 0; i < 2; i++) {
        const p = partesNoFuso(new Date(palpite), fuso);
        const visto = Date.UTC(p.ano, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo);
        palpite += alvo - visto;
    }
    return new Date(palpite);
}
/**
 * Datas de calendário (prazo, admissão, ausência) são colunas DATE que o
 * Prisma devolve como meia-noite UTC. A ida e volta para "YYYY-MM-DD" não
 * depende de fuso: a data é a mesma em qualquer relógio.
 */
export function dataIsoDeCalendario(data) {
    return data.toISOString().slice(0, 10);
}
/** "YYYY-MM-DD" → meia-noite UTC, como a coluna DATE guarda. */
export function calendarioDeDataIso(iso) {
    const data = new Date(`${iso}T00:00:00.000Z`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso) || Number.isNaN(data.getTime())) {
        throw new RangeError(`Data inválida: ${iso}`);
    }
    return data;
}
/** Início do dia local que contém `instante`. */
export function inicioDoDia(instante, fuso) {
    const p = partesNoFuso(instante, fuso);
    return instanteNoFuso(fuso, p.ano, p.mes, p.dia);
}
/** Soma dias ao relógio de parede (respeita o fuso, não só 24 h). */
export function somarDias(instante, dias, fuso) {
    const p = partesNoFuso(instante, fuso);
    return instanteNoFuso(fuso, p.ano, p.mes, p.dia + dias, p.hora, p.minuto, p.segundo);
}
/**
 * Limites do período que contém `referencia`, no fuso da fábrica.
 * Semana começa na segunda-feira (semana de trabalho da fábrica); quinzena
 * é 1–15 e 16–fim do mês (o corte usual do bônus quinzenal).
 */
export function limitesDoPeriodo(periodo, referencia, fuso) {
    const p = partesNoFuso(referencia, fuso);
    switch (periodo) {
        case 'dia': {
            const inicio = instanteNoFuso(fuso, p.ano, p.mes, p.dia);
            return { inicio, fim: instanteNoFuso(fuso, p.ano, p.mes, p.dia + 1) };
        }
        case 'semana': {
            const desdeSegunda = (p.diaSemana + 6) % 7;
            const inicio = instanteNoFuso(fuso, p.ano, p.mes, p.dia - desdeSegunda);
            return { inicio, fim: instanteNoFuso(fuso, p.ano, p.mes, p.dia - desdeSegunda + 7) };
        }
        case 'quinzena':
            return p.dia <= 15
                ? {
                    inicio: instanteNoFuso(fuso, p.ano, p.mes, 1),
                    fim: instanteNoFuso(fuso, p.ano, p.mes, 16),
                }
                : {
                    inicio: instanteNoFuso(fuso, p.ano, p.mes, 16),
                    fim: instanteNoFuso(fuso, p.ano, p.mes + 1, 1),
                };
        case 'mes':
            return {
                inicio: instanteNoFuso(fuso, p.ano, p.mes, 1),
                fim: instanteNoFuso(fuso, p.ano, p.mes + 1, 1),
            };
    }
}
/** Competência "YYYY-MM" (fechamento mensal) de um instante no fuso. */
export function competenciaDe(instante, fuso) {
    const p = partesNoFuso(instante, fuso);
    return `${p.ano}-${String(p.mes).padStart(2, '0')}`;
}
/** Intervalo do mês de uma competência "YYYY-MM" no fuso da fábrica. */
export function intervaloDaCompetencia(competencia, fuso) {
    const m = /^(\d{4})-(\d{2})$/.exec(competencia);
    if (!m)
        throw new RangeError(`Competência inválida: "${competencia}" (use YYYY-MM)`);
    const ano = Number(m[1]);
    const mes = Number(m[2]);
    if (mes < 1 || mes > 12)
        throw new RangeError(`Competência inválida: "${competencia}"`);
    return { inicio: instanteNoFuso(fuso, ano, mes, 1), fim: instanteNoFuso(fuso, ano, mes + 1, 1) };
}
/** Fatia um intervalo em dias locais (a primeira e a última podem ser parciais). */
export function diasNoIntervalo(intervalo, fuso) {
    const fatias = [];
    if (intervalo.fim <= intervalo.inicio)
        return fatias;
    let cursor = inicioDoDia(intervalo.inicio, fuso);
    while (cursor < intervalo.fim) {
        const proximo = somarDias(cursor, 1, fuso);
        const p = partesNoFuso(cursor, fuso);
        fatias.push({
            inicio: cursor > intervalo.inicio ? cursor : intervalo.inicio,
            fim: proximo < intervalo.fim ? proximo : intervalo.fim,
            diaSemana: p.diaSemana,
            data: `${p.ano}-${String(p.mes).padStart(2, '0')}-${String(p.dia).padStart(2, '0')}`,
        });
        cursor = proximo;
    }
    return fatias;
}
/** Segundos da interseção entre dois intervalos (0 se não se tocam). */
export function segundosNaIntersecao(a, b) {
    const inicio = Math.max(a.inicio.getTime(), b.inicio.getTime());
    const fim = Math.min(a.fim.getTime(), b.fim.getTime());
    return Math.max(0, (fim - inicio) / 1000);
}
//# sourceMappingURL=periodo.js.map